
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import { Badge } from '@/components/ui/badge';
import { CalendarX, Plus, Trash2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { getBlockedDates, addBlockedDate, removeBlockedDate, BlockedDate } from '@/utils/blockedDatesUtils';
import { format } from 'date-fns';

const BlockedDatesManager = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [blockedDates, setBlockedDates] = useState<BlockedDate[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date>();
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      loadBlockedDates();
    }
  }, [user]);

  const loadBlockedDates = async () => {
    if (!user?.id) return;

    setLoading(true);
    try {
      const result = await getBlockedDates(user.id);
      if (result.success && result.data) {
        setBlockedDates(result.data);
      }
    } catch (error) {
      console.error('Error loading blocked dates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddBlockedDate = async () => {
    if (!selectedDate || !user?.id) return;

    setSaving(true);
    try {
      const blockedDate: BlockedDate = {
        mentor_id: user.id,
        date: format(selectedDate, 'yyyy-MM-dd'),
        reason: reason || undefined
      };

      const result = await addBlockedDate(blockedDate);
      if (result.success) {
        toast({
          title: "Date Blocked",
          description: `${format(selectedDate, 'PPP')} has been blocked.`,
        });
        setSelectedDate(undefined);
        setReason('');
        loadBlockedDates();
      } else {
        throw new Error('Failed to block date');
      }
    } catch (error) {
      console.error('Error blocking date:', error);
      toast({
        title: "Error",
        description: "Failed to block date. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveBlockedDate = async (id: string) => {
    try {
      const result = await removeBlockedDate(id);
      if (result.success) {
        toast({
          title: "Date Unblocked",
          description: "The date has been unblocked.",
        });
        loadBlockedDates();
      } else {
        throw new Error('Failed to unblock date');
      }
    } catch (error) {
      console.error('Error unblocking date:', error);
      toast({
        title: "Error",
        description: "Failed to unblock date. Please try again.",
        variant: "destructive",
      });
    }
  };

  const blockedDateStrings = blockedDates.map(bd => bd.date);

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="w-8 h-8 border-4 border-emu-blue border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CalendarX className="h-5 w-5" />
            Block Specific Dates
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <Label>Select Date to Block</Label>
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={setSelectedDate}
                disabled={(date) => 
                  date < new Date() || 
                  blockedDateStrings.includes(format(date, 'yyyy-MM-dd'))
                }
                className="rounded-md border"
              />
            </div>

            <div className="space-y-4">
              <div>
                <Label htmlFor="reason">Reason (Optional)</Label>
                <Textarea
                  id="reason"
                  placeholder="e.g., Vacation, Holiday, Personal time..."
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
              </div>

              <Button 
                onClick={handleAddBlockedDate}
                disabled={!selectedDate || saving}
                className="w-full"
              >
                <Plus className="h-4 w-4 mr-2" />
                {saving ? "Blocking..." : "Block Date"}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Blocked Dates</CardTitle>
        </CardHeader>
        <CardContent>
          {blockedDates.length === 0 ? (
            <p className="text-gray-500 text-center py-4">No blocked dates</p>
          ) : (
            <div className="space-y-3">
              {blockedDates.map((blockedDate) => (
                <div key={blockedDate.id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <Badge variant="secondary" className="mb-1">
                      {format(new Date(blockedDate.date), 'PPP')}
                    </Badge>
                    {blockedDate.reason && (
                      <p className="text-sm text-gray-600">{blockedDate.reason}</p>
                    )}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleRemoveBlockedDate(blockedDate.id!)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BlockedDatesManager;
